import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Flex, Heading, Label, Text, TextAreaField } from '@aws-amplify/ui-react';
import { PublicUserProfile } from '../../types/user';
import StarRating from './StarRating';
import PlaceDataUtils, { RatingDefinition, RatingStatus } from '../../utils/PlaceDataUtils';
import { AuthUser } from 'aws-amplify/auth';
import I18nTextUtils, { LanguageDetail } from '../../utils/i18n/I18nTextUtils';
import InputSanitizationUtils from '../../utils/InputSanitizationUtils';
import './PlaceRating.css';

interface PlaceRatingProps {
    placeId: string;
    user: AuthUser;
    onRatingAdded?: () => void;
    onRatingDeleted?: () => void;
}

export default function PlaceRating({ placeId, user, onRatingAdded, onRatingDeleted }: PlaceRatingProps) {
    const { t } = useTranslation();
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [existingRating, setExistingRating] = useState<RatingDefinition | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        fetchExistingRating();
    }, [placeId]);

    const fetchExistingRating = async () => {
        try {
            const rating = await PlaceDataUtils.getUserPlaceRating(placeId, user);
            if (rating) {
                setExistingRating(rating);
                setRating(rating.rating);
                setComment(rating.ratingText[0]?.content || '');
            }
        } catch (error) {
            console.error('Error fetching rating:', error);
        }
    };

    const handleSubmit = async () => {
        if (!rating || isSubmitting) return;

        // Sanitize rating text before submission
        const sanitizedComment = InputSanitizationUtils.sanitizePlainText(comment.trim());

        setIsSubmitting(true);
        try {
            await PlaceDataUtils.createNewRating({
                rating,
                ratingText: sanitizedComment,
                placeId,
                creatorUserId: user.userId,
                status: RatingStatus.ACTIVE
            }, I18nTextUtils.getCurrentLanguageDetail());

            const newRating = await PlaceDataUtils.getUserPlaceRating(placeId, user);
            if (newRating) {
                setExistingRating(newRating);
                setIsExpanded(false);
                onRatingAdded?.();
            }
        } catch (error) {
            console.error('Error creating rating:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!existingRating?.id || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await PlaceDataUtils.deleteUserRating(existingRating.id, user);
            setExistingRating(null);
            setRating(0);
            setComment('');
            onRatingDeleted?.();
        } catch (error) {
            console.error('Error deleting rating:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTextAreaResize = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    return (
        <Flex
            direction="column" 
            gap="small"
            className={`rating-container ${existingRating ? 'rating-container--prefilled' : 'rating-container--new'}`}
        >
            <Flex
                className={`rating-header ${existingRating ? 'rating-header--prefilled' : 'rating-header--new'}`}
                justifyContent="space-between"
                alignItems="center"
            wrap="wrap"
            >
                <Text className="rating-title">
                    {existingRating ? t('place.rating.section-title') : t('place.rating.add-rating')}
                </Text>
                {existingRating && (
                    <Flex gap="small" alignItems="center" className="rating-toggle-container">
                        <Text className="rating-status">{t('place.rating.already-rated')}</Text>
                        <Button
                            className="rating-toggle-button"
                            onClick={() => setIsExpanded(!isExpanded)}
                            aria-label={isExpanded ? t('place.rating.collapse') : t('place.rating.expand')}
                        >
                            {isExpanded ? '▲' : '▼'}
                        </Button>
                    </Flex>
                )}
                <StarRating rating={existingRating ? existingRating.rating : rating} onChange={setRating} readOnly={!!existingRating} />
            </Flex>

            {existingRating ? (
                <Flex
                    className={`rating-content ${isExpanded ? 'expanded' : ''}`}
                    direction="column"
                    alignItems="flex-start"
                    gap="small"
                    padding="small"
                >
                    <TextAreaField
                        className="rating-text-textarea prefilled"
                        label=""
                        labelHidden={true}
                        value={I18nTextUtils.resolveInternationalizedText(existingRating.ratingText)}
                        isReadOnly={true}
                        whiteSpace="pre-wrap"
                        textAlign="left"
                    />
                    <Button
                        className="rating-delete-button"
                        variation="destructive"
                        size="small"
                        onClick={handleDelete}
                        isLoading={isSubmitting}
                        width="100%"
                    >
                        {t('place.rating.delete')}
                    </Button>
                </Flex>
            ) : (
                rating > 0 && (
                    <>
                        <TextAreaField
                            // className="rating-comment-textarea"
                            className="rating-text-textarea new"
                            label=""
                            labelHidden={true}
                            placeholder={t('place.rating.comment-placeholder')}
                            value={comment}
                            onChange={(e) => {
                                setComment(e.target.value);
                                handleTextAreaResize();
                            }}
                            ref={textareaRef}
                        />
                        <Flex justifyContent="flex-end" gap="small" className="rating-submit-container">
                            <Button
                                variation="destructive"
                                onClick={() => {
                                    setRating(0);
                                    setComment('');
                                }}
                                className="rating-cancel-button"
                            >
                                {t('place.rating.cancel')}
                            </Button>
                            <Button
                                variation="primary"
                                onClick={handleSubmit}
                                isLoading={isSubmitting}
                                isDisabled={!rating || isSubmitting}
                                className="rating-submit-button"
                            >
                                {t('place.rating.submit')}
                            </Button>
                        </Flex>
                    </>
                )
            )}
        </Flex>
    );
} 